import React, { Fragment, memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Cell, Pie, PieChart } from 'recharts';
import { chartData, colors } from './config';
import Tooltip from '../Tooltip';

import ps from '../style.module.scss';
import s from './style.module.scss';
import { withTranslation } from 'react-i18next';
import i18next from 'i18next';
import { withRouter } from 'react-router-dom';
import { routers } from 'settings';
import NavigationIcon from '../../../components/Icons/NavigationIcon';
import { patientDashboardErrorGroups } from '../../PatientDashboard/Config/constants';

const TooltipContent = (props) => {
	const { active } = props;

	if (active) {
		const { payload = [], total = 1, totalConnected = 1 } = props;
		const first = payload?.[0] || {};
		const value = first?.value ?? 0;
		const units = totalConnected ? (value / totalConnected) * 100 : 0;

		return (
			<div className={s.tooltip}>
				<h3 className={s.tipTitle}>
					<i style={{ backgroundColor: first?.payload?.fill }} />
					<div style={{ fontSize: '10px' }}>{i18next.t(first?.name || '')}</div>
				</h3>
				<ul className={s.tipList}>
					<li>
						{i18next.t('Occurrences')} <strong>{value}</strong>
					</li>
					<li>
						{i18next.t('total_errors')}{' '}
						<strong>
							{(total ? ((value / total) * 100).toFixed(1) : '0.0').replace(
								i18next.language === 'fr' ? '.' : 'x',
								',',
							)}{' '}
							%
						</strong>
					</li>
					<li>
						{i18next.t('total_units')}{' '}
						<strong>
							{units < 1
								? '<1'
								: units !== 0
								  ? units
											.toFixed(1)
											.replace(i18next.language === 'fr' ? '.' : 'x', ',')
								  : 0}{' '}
							% <span className={s.asterix}>*</span>
						</strong>
					</li>
					<li className={s.notes}>
						<span className={s.asterix}> * </span>
						{i18next.t('total_number_of_apps')}
					</li>
				</ul>
			</div>
		);
	}

	return null;
};

const RecentError = (props) => {
	const [tip, setTip] = useState();
	const pieChartRef = useRef(null);

	const [isTooltipActive, setTooltipState] = useState(false);
	const [tooltipPayload, setTooltipPayload] = useState({});
	const [coordinate, setTooltipCoordinate] = useState({});

	const { data, period, total, params } = props;
	// Group data by common description (or code when no concentrator_error)
	const groupKeyFor = (el) => {
		if (!el) return '';
		if (!el.concentrator_error) return String(el.code);
		return props.env === 'engineering'
			? el.concentrator_error.description_engineer
			: el.concentrator_error.description;
	};

	const groupedData = (() => {
		if (!Array.isArray(data) || !data.length) return [];
		const map = new Map();
		data.forEach((el) => {
			const key = groupKeyFor(el);
			const amt = Number(el?.amount) || 0;
			if (map.has(key)) {
				const existing = map.get(key);
				existing.amount = (Number(existing.amount) || 0) + amt;
				existing.items = existing.items || [];
				existing.items.push(el);
				existing.ids = existing.ids || [];
				existing.ids.push(el?.error_id);
			} else {
				map.set(
					key,
					Object.assign({}, el, {
						amount: amt,
						groupKey: key,
						items: [el],
						ids: [el?.error_id],
					}),
				);
			}
		});
		return Array.from(map.values()).sort(
			(a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0),
		);
	})();

	const totalAmount = groupedData.reduce((acc, val) => (acc += val.amount), 0);

	const mouseEnterHandler = (e, data) => {
		if (e && typeof e.stopPropagation === 'function') {
			e.stopPropagation();
		}
		setTip({
			x: e?.currentTarget?.parentNode?.offsetLeft ?? 0,
			data: {
				groupKey: data?.groupKey || groupKeyFor(data),
				amount: data?.amount ?? 0,
				items: data?.items || [data],
			},
		});
	};

	const setViewBox = () => {
		return {
			width: pieChartRef.current?.container?.clientWidth ?? 0,
			height: pieChartRef.current?.container?.clientHeight ?? 0,
		};
	};

	const showTooltip = (e) => {
		setTooltipPayload(e?.tooltipPayload ?? {});
		setTooltipState(true);
		setTooltipCoordinate(e?.tooltipPosition ?? {});
	};

	const errorListClickHandler = (index) => {
		const el = groupedData[index] || data[index];
		// Preserve existing callback for potential side-effects
		if (props.errorClick) {
			props.errorClick(el, params);
		}
		// Navigate to patient dashboard only for supported error groups
		if (props.history && isSupportedForPatientDashboard(el)) {
			const representative = (el.items && el.items[0]) || el;
			const { error_id, code } = representative || {};
			const qs = new URLSearchParams();
			if (error_id) qs.set('error_id', String(error_id));
			if (code) qs.set('error_code', String(code));
			if (
				Array.isArray(props.selectedModelIds) &&
				props.selectedModelIds.length
			) {
				qs.set('model_id', props.selectedModelIds.join(','));
			}
			props.history.push(`${routers.patientDashboardIndex}?${qs.toString()}`);
		}
	};

	const getDescription = (el) => {
		const representative = (el.items && el.items[0]) || el;
		if (!representative.concentrator_error) return representative.code;
		const err =
			props.env === 'engineering'
				? representative?.concentrator_error?.description_engineer
				: representative?.concentrator_error?.description;
		return i18next.t(err);
	};

	const isSupportedForPatientDashboard = (el) => {
		const items = el.items || [el];
		return items.some((item) => {
			if (!item.concentrator_error) return false;
			const group =
				props.env === 'engineering'
					? item?.concentrator_error?.description_engineer
					: item?.concentrator_error?.description;
			return patientDashboardErrorGroups.includes(group);
		});
	};

	const content = (
		<Fragment>
			<div className={s.chart} onMouseLeave={() => setTooltipState(false)}>
				<PieChart width={210} height={210} ref={pieChartRef}>
					<Pie
						data={chartData(groupedData)}
						dataKey='value'
						cx={100}
						cy={100}
						innerRadius={50}
						outerRadius={100}
						onMouseEnter={showTooltip}
					>
						{groupedData.map((entry, index) => (
							<Cell
								key={index}
								fill={colors[entry.error_id || (entry.ids && entry.ids[0])]}
								stroke='none'
							/>
						))}
					</Pie>
				</PieChart>
				{isTooltipActive ? (
					<Tooltip
						auto={true}
						viewBox={setViewBox()}
						active={isTooltipActive}
						coordinates={coordinate}
						offset={10}
						onMouseLeave={() => setTooltipState(false)}
					>
						<TooltipContent
							total={totalAmount}
							totalConnected={total}
							active={isTooltipActive}
							payload={tooltipPayload}
						/>
					</Tooltip>
				) : null}
			</div>

			<ul
				className={`${s.errorList} ${props.width === 'full' && s.twoColumns}`}
				onMouseLeave={() => setTip()}
			>
				{groupedData.map((el, index) => (
					<li
						key={index}
						className={`${s.item} ${props.width === 'full' && s.width}`}
						onClick={() => errorListClickHandler(index)}
						role='button'
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								errorListClickHandler(index);
							}
						}}
					>
						<span
							className={s.marker}
							style={{ backgroundColor: colors[el?.error_id ?? el?.ids?.[0]] }}
						/>
						{getDescription(el)}
						<span className={s.dots} />
						<span className={s.countWrap}>
							<strong className={s.countText}>{el?.amount ?? 0}</strong>
							{isSupportedForPatientDashboard(el) && (
								<NavigationIcon className={s.navIcon} />
							)}
						</span>
						<span className={s.scale}>
							<span
								onMouseEnter={(e) => mouseEnterHandler(e, el)}
								style={{
									width: `${
										((Number(el?.amount) || 0) /
											(Number(groupedData?.[0]?.amount) || 1)) *
										100
									}%`,
									backgroundColor: colors[el?.error_id ?? el?.ids?.[0]],
								}}
							/>
						</span>
						{tip?.data?.groupKey === el.groupKey && (
							<div className={s.tooltip} style={{ left: `${tip?.x ?? 0}px` }}>
								<h3 className={s.tipTitle}>
									<i
										style={{
											backgroundColor: colors[el?.error_id ?? el?.ids?.[0]],
										}}
									/>
									{el?.items?.[0]?.concentrator_error
										? i18next.t(el.items[0].concentrator_error.description)
										: i18next.t(el?.items?.[0]?.code ?? el?.code ?? '')}
								</h3>
								<ul className={s.tipList}>
									<li>
										{i18next.t('Occurrences')}{' '}
										<strong>{tip?.data?.amount ?? 0}</strong>
									</li>
									<li>
										{i18next.t('total_errors')}{' '}
										<strong>
											{(((tip?.data?.amount ?? 0) / (totalAmount || 1)) * 100)
												.toFixed(1)
												.replace(
													i18next.language === 'fr' ? '.' : 'x',
													',',
												)}{' '}
											%
										</strong>
									</li>
									<li>
										{' '}
										{i18next.t('of_total_units')}
										<strong>
											{((total && ((tip?.data?.amount ?? 0) / total) * 100) ||
												0) < 1
												? '<1'
												: ((tip?.data?.amount ?? 0) / (total || 1)) * 100 !== 0
												  ? (((tip?.data?.amount ?? 0) / (total || 1)) * 100)
															.toFixed(1)
															.replace(
																i18next.language === 'fr' ? '.' : 'x',
																',',
															)
												  : 0}
											% <span className={s.asterix}>*</span>
										</strong>
									</li>
									<li className={s.notes}>
										<span className={s.asterix}> * </span>
										{i18next.t('total_number_of_apps')}
									</li>
								</ul>
							</div>
						)}
					</li>
				))}
			</ul>
		</Fragment>
	);

	return (
		<div className={ps[props.width]}>
			<div className={ps.panel}>
				<h2 className={ps.title}>
					{i18next.t('unresolved_errors')}
					<span>{i18next.t(period)}</span>
				</h2>

				<div className={s.inner}>
					{groupedData.length ? (
						content
					) : (
						<div className={s.noShow}>{i18next.t('nothing_to_show')}</div>
					)}
				</div>
			</div>
		</div>
	);
};

RecentError.defaultProps = {
	data: [],
	period: '',
	total: 1,
	width: 'twoThird',
	errorClick: () => {},
	env: 'dashboard',
	selectedModelIds: [],
};

RecentError.propTypes = {
	data: PropTypes.array,
	period: PropTypes.string,
	total: PropTypes.number,
	width: PropTypes.string,
	errorClick: PropTypes.func,
	history: PropTypes.object,
	selectedModelIds: PropTypes.array,
};

export default memo(withRouter(withTranslation()(RecentError)));
